import { Injectable, signal } from '@angular/core';
import { filter, take } from 'rxjs';
import { CopilotFacadeService } from './copilot-facade.service';

// ── Data models ───────────────────────────────────────────────────────────────

export interface Playbook {
  id: string;
  label: string;
  icon: string;       // pi pi-* icon class
  prompt: string;     // full structured prompt sent to the LLM
}

export interface SlashCommand {
  id: string;
  command: string;    // e.g. "/email"
  description: string;
  isDemo: boolean;
  templateText?: string; // text to insert into composer (for demo commands)
}

// ── Playbook catalog ──────────────────────────────────────────────────────────

export const PLAYBOOKS: Playbook[] = [
  {
    id: 'shift-handoff-summary',
    label: 'Shift Handoff Summary',
    icon: 'pi pi-refresh',
    prompt: `Summarize the current client state for shift handoff.

Respond using this structure:
- Current Situation (1-2 bullets)
- Active Risks (max 3)
- Open Actions (with owner)
- Next Best Action in Next Shift (1 clear recommendation)

Be concise and operational. No narrative paragraphs.`,
  },
  {
    id: 'care-history-snapshot',
    label: 'Care History Snapshot',
    icon: 'pi pi-book',
    prompt: `Summarize this client's care history.

Respond using:
- Engagement Start Date:
- Key Milestones:
- Escalations / High-Risk Events:
- Pattern Observations (if any):
- Current Phase of Care:

Include only the most relevant historical items.`,
  },
  {
    id: 'changes-since-last-fa-touch',
    label: 'Changes Since Last FA Touch',
    icon: 'pi pi-history',
    prompt: `What has changed since the last FA interaction?

Respond using:
- Situation:
- Recent Changes:
- New or Escalated Risks:
- Recommended Next Action (1-2 only)

Be concise.`,
  },
  {
    id: 'risk-triage',
    label: 'Risk Triage',
    icon: 'pi pi-exclamation-triangle',
    prompt: `Assess current client risks.

Respond using:
- Immediate (requires action today)
- Emerging (monitor closely)
- Stable but Watchlist

For each risk include:
- Why it matters
- Suggested mitigation`,
  },
  {
    id: 'next-10-minutes',
    label: 'Next 10 Minutes',
    icon: 'pi pi-clock',
    prompt: `You are the assigned FA mid-shift.

What are the highest-leverage actions to take in the next 10 minutes?

Respond using:
- Action:
- Why:
- Expected Outcome:

Limit to 3 actions max.`,
  },
  {
    id: 'draft-client-reply',
    label: 'Draft Client Reply',
    icon: 'pi pi-envelope',
    prompt: `Draft a client-ready response.

Structure:
- Acknowledge & Empathize
- Current Status
- Clear Next Steps
- Timeline / Expectations

Keep under 150 words.
Avoid internal terminology.
Be concise and reassuring.`,
  },
  {
    id: 'opportunity-snapshot',
    label: 'Opportunity Snapshot',
    icon: 'pi pi-chart-line',
    prompt: `Summarize open opportunities.

For each opportunity include:
- Stage:
- Blockers:
- Probability (High / Medium / Low):
- Next Concrete Step:

Include active opportunities only.`,
  },
  {
    id: 'pre-contact-brief',
    label: 'Pre-Contact Brief',
    icon: 'pi pi-user',
    prompt: `Provide a pre-contact briefing.

Structure:
- Client Emotional Context:
- Sensitive Topics:
- Key Open Threads:
- Do Not Miss:
- Goal of This Interaction:

Include only decision-enabling information.`,
  },
  {
    id: 'escalation-check',
    label: 'Escalation Check',
    icon: 'pi pi-bolt',
    prompt: `Determine whether this situation requires escalation.

Respond:
- Escalation Needed? (Yes / No)
- If Yes -> Why:
- Recommended Escalation Path:
- If No -> Why not:

Be decisive.`,
  },
  {
    id: 'client-stability-score',
    label: 'Client Stability Score',
    icon: 'pi pi-heart',
    prompt: `Assess overall client stability.

Respond:
- Stability Rating (1-5):
- Primary Drivers of Risk:
- Positive Signals:
- Trend Direction (Improving / Stable / Declining):
- Immediate Watch Item:

Be analytical and concise.`,
  },
  {
    id: 'case-complexity',
    label: 'Case Complexity',
    icon: 'pi pi-sitemap',
    prompt: `Assess case complexity.

Respond:
- Complexity Level (Low / Medium / High):
- Drivers of Complexity:
- Estimated Effort This Week:
- Should This Be Reassigned? (Yes / No + Why):

Be objective.`,
  },
];

// ── Slash command catalog ─────────────────────────────────────────────────────

export const SLASH_COMMANDS: SlashCommand[] = [
  {
    id: 'email',
    command: '/email',
    description: 'Insert a draft email template',
    isDemo: true,
    templateText: `Subject: Following up on your account

Hi [Client Name],

I wanted to follow up on our recent conversation and provide a quick update on your account.

[Insert key update here]

Please don't hesitate to reach out if you have any questions. I'm here to help.

Best regards,
[FA Name]`,
  },
  {
    id: 'loan-card',
    command: '/loan-card',
    description: 'Preview a loan card action',
    isDemo: true,
  },
];

// ── Service ───────────────────────────────────────────────────────────────────

/**
 * Manages the playbook catalog, slash command catalog, and in-flight execution state.
 *
 * Playbooks: REAL — dispatches through CopilotFacadeService.sendMessage().
 * Slash commands: DEMO-ONLY — template insertion or toast confirmation.
 */
@Injectable({ providedIn: 'root' })
export class CopilotPlaybooksService {
  private readonly _inFlight = signal<Set<string>>(new Set());
  readonly inFlight = this._inFlight.asReadonly();

  readonly playbooks: Playbook[] = PLAYBOOKS;
  readonly slashCommands: SlashCommand[] = SLASH_COMMANDS;

  isInFlight(id: string): boolean {
    return this._inFlight().has(id);
  }

  /**
   * Dispatches a playbook prompt through the real Copilot backend pipeline.
   * Marks the playbook as in-flight until streaming completes.
   */
  runPlaybook(
    id: string,
    facade: CopilotFacadeService,
    clientId: string,
    conversationId: string,
    loggedInUserId: string
  ): void {
    if (this._inFlight().has(id)) return; // prevent duplicate dispatch

    const playbook = this.playbooks.find((p) => p.id === id);
    if (!playbook) return;

    // Mark in-flight
    this._inFlight.update((s) => new Set([...s, id]));

    // Dispatch through the real pipeline
    facade.sendMessage(clientId, conversationId, playbook.prompt, undefined, loggedInUserId);

    // Clear in-flight when streaming ends for this conversation
    facade
      .isStreaming$(conversationId)
      .pipe(
        filter((streaming) => !streaming),
        take(1)
      )
      .subscribe(() => {
        this._inFlight.update((s) => {
          const next = new Set(s);
          next.delete(id);
          return next;
        });
      });
  }

  getSlashCommand(id: string): SlashCommand | undefined {
    return this.slashCommands.find((c) => c.id === id);
  }

  getFilteredSlashCommands(query: string): SlashCommand[] {
    const q = query.toLowerCase().replace(/^\//, '');
    if (!q) return this.slashCommands;
    return this.slashCommands.filter(
      (c) =>
        c.command.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q)
    );
  }
}
